import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import postAddUser from '../../../api/private/users_management/posts/postAddUser';
// import Modal from '../../../components/Modal';

function CreateNewUserModal({
  open,
  handleClose,
  userTypesMapping,
  apiAccessTypesMapping,
  userPermissionMapping,
  usersCount,
  setUsersCount,
}) {
  const emailRegex = /(?:[a-z0-9_!#$%&'*+/=?^`{|}~-]+(?:\.[a-z0-9_!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g;

  const user = useSelector((state) => state.user.userProfile);
  const sites = useSelector((state) => state.fascias);
  const groups = useSelector((state) => state.groups.groupList);

  const [email, setEmail] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [userType, setUserType] = React.useState(userTypesMapping.ActiveDirectoryUser);
  const [chosenGroup, setChosenGroup] = React.useState([]);
  const [apiAccessType, setApiAccessType] = React.useState(apiAccessTypesMapping.PrivateApi);
  const [userPermission, setUserPermission] = React.useState('');
  const [siteIds, setSitesIds] = React.useState([]);
  const [selectedGroup, setSelectedGroup] = React.useState([]);
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [customOptions, setCustomOptions] = React.useState([]);
  const [errorExists, setErrorExists] = React.useState(false);

  const allSelected = selectedOptions?.length === chosenGroup?.SiteIds?.length;
  const isIndeterminate = selectedOptions.length > 0 && !allSelected;

  const disableCreateButton = () => !username
      || !userType
      || !apiAccessType
      || !email
      || errorExists
      || !chosenGroup
      || !userPermission
      || siteIds?.length === 0;

  const addUser = async () => {
    try {
      await postAddUser(username, userType, userPermission, siteIds, apiAccessType, email, selectedGroup.Id, selectedGroup.GroupName);
      handleClose(false);
      toast.success('Successfully created user');
      setUsersCount(usersCount + 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create user');
    }
  };

  const handleFaciaChange = (event, values) => {
    const isSelectAllSelected = values.some((option) => option.label === 'Select All');
    const allOptionsSelected = customOptions.filter((option) => option.label !== 'Select All');

    if (isSelectAllSelected) {
      if (selectedOptions.length === allOptionsSelected.length) {
        setSelectedOptions([]);
        setSitesIds([]);
      } else {
        setSelectedOptions(allOptionsSelected);
        setSitesIds(allOptionsSelected.map((option) => option.value));
      }
    } else {
      setSelectedOptions(values.filter((option) => option.label !== 'Select All'));
      setSitesIds(values.filter((option) => option.label !== 'Select All').map((option) => option.value));
    }
  };

  const handleGroupChange = (value) => {
    setSelectedOptions([]);
    const sitesFlattened = sites.flatMap((obj) => obj.Sites);
    const groupSites = sitesFlattened
      .filter((obj) => value?.SiteIds?.includes(obj?.SiteId))
      .map((obj) => ({
        label: obj.Name,
        value: obj.SiteId,
      }));
    setCustomOptions([{ label: 'Select All', value: 'all' }, ...groupSites]);
    setSelectedGroup(value);
    setSitesIds([]);
  };

  const handleEmailChange = (value) => {
    setEmail(value);
  };

  React.useEffect(() => {
    if ((!email.includes('@jdplc.com') && !email.includes('@cosmossport.gr') && !email.includes('@globallogic.com'))
      && userType === 1) {
      setErrorExists(true);
    } else {
      setErrorExists(false);
    }
  }, [email, userType]);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '40vw' },
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle component="h5" sx={{ textAlign: 'center', fontSize: '2rem' }}>
        Add New User
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: 'auto',
          maxHeight: '125vh',
          pr: 1,
          pb: 0,
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="h6">User Details</Typography>

          <TextField
            required
            label="Email"
            fullWidth
            size="small"
            type="email"
            error={email.length > 0 && (!email.match(emailRegex) || errorExists)}
            onChange={(event) => handleEmailChange(event.target.value)}
            helperText={email.length > 0 && errorExists ? 'Invalid email address' : ''}
          />

          <TextField
            required
            label="Full Name"
            fullWidth
            size="small"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            InputLabelProps={{ shrink: username?.length > 0 }}
          />

          <Autocomplete
            fullWidth
            id="group-select"
            size="small"
            options={
              user.user_permission === 'Admin'
                ? groups
                : groups.filter((obj) => obj.Id === user.user_groupId)
            }
            getOptionLabel={(option) => option.GroupName || ''}
            onChange={(event, value) => {
              setChosenGroup(value);
              handleGroupChange(value);
            }}
            renderInput={(params) => <TextField required {...params} label="Group" />}
          />

          <FormControl fullWidth required size="small">
            <InputLabel>User Permission</InputLabel>
            <Select
              value={userPermission}
              label="User Permission"
              onChange={(event) => setUserPermission(event.target.value)}
            >
              {Object.entries(userPermissionMapping)
                .filter(([key]) => (user.user_permission === 'GroupAdmin' ? key !== 'Admin' : true))
                .map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {key}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Typography variant="h6">Settings</Typography>

          <FormControl fullWidth size="small">
            <InputLabel>User Type</InputLabel>
            <Select
              value={userType}
              label="User Type"
              onChange={(event) => setUserType(event.target.value)}
            >
              {Object.entries(userTypesMapping).map(([key, value]) => (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Access Type</InputLabel>
            <Select
              value={apiAccessType}
              label="Access Type"
              onChange={(event) => setApiAccessType(event.target.value)}
            >
              {Object.entries(apiAccessTypesMapping).map(([key, value]) => (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="h6">Access</Typography>

          <Autocomplete
            fullWidth
            multiple
            size="small"
            options={customOptions}
            value={selectedOptions}
            onChange={handleFaciaChange}
            disableCloseOnSelect
            getOptionLabel={(option) => option.label}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={option.label === 'Select All' ? allSelected : selected}
                  indeterminate={option.label === 'Select All' && isIndeterminate}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField required {...params} variant="outlined" label="Facias" />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1, m: 1 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={addUser} disabled={disableCreateButton()}>
          Save
        </Button>
      </DialogActions>

    </Dialog>

  );
}

export default CreateNewUserModal;
